/** @format */
import {CartCheckoutAddressModal} from 'frontier/cart/components/addresses/form_container';
import {CartCheckoutContext} from 'frontier/cart/components/cart/cart_checkout_context';
import {useAddressDelete} from 'frontier/cart/components/addresses/use_address_delete';
import {prettyAddress, singlePrettyAddress} from 'frontier/cart/helpers/format';
import {Button, FilterInput} from '@sttark/design-system';
import React, {useState, useContext, useRef, useEffect} from 'react';
import {If, IfElse} from 'frontier-utilities';
import SuperAgent from 'superagent';

export default function SingleShipmentAddresses({shipmentId, addressId}) {
    const ctx = useContext(CartCheckoutContext);
    const [isAddingNewAddress, setIsAddingNewAddress] = useState(false);
    const [editingAddressId, setEditingAddressId] = useState(false);
    const [selectedAddressId, setSelectedAddress] = useState(addressId);
    const [isUpdating, setIsUpdating] = useState(false);
    const {deleteAddressById} = useAddressDelete();

    const fetchRequestRef = useRef(false);

    async function handleChooseShippingAddress(value) {
        const address = ctx.addresses.find(({id}) => id === value);

        if (!address.company) {
            toggleInvalidAddress();
            return;
        }

        setSelectedAddress(value);
        setIsUpdating(true);
        const newAddressId = parseInt(value);
        if (!isNaN(newAddressId)) {
            if (fetchRequestRef.current) {
                fetchRequestRef.current.abort();
            }
            fetchRequestRef.current = SuperAgent.post(
                `/account/shipments/set-shipping-address/${shipmentId}`
            )
                .set('accept', 'application/json')
                .send({address_id: newAddressId});

            const {
                body: {cart}
            } = await fetchRequestRef.current;

            const ShipmentIndex = ctx.shipments.findIndex(({id}) => id === shipmentId);

            const selectedAddress = ctx.addresses.find(({id}) => id === newAddressId);

            ctx.onUpdateWithoutSave({
                cart: {
                    shipping: {$set: cart.shipping},
                    tax: {$set: cart.tax},
                    total: {$set: cart.total}
                },
                shipments: {
                    [ShipmentIndex]: {
                        address_id: {$set: newAddressId},
                        shipping_method_code: {
                            $set: ''
                        },
                        shipping_method_provider: {
                            $set: ''
                        },
                        shipping_method_name: {
                            $set: ''
                        },
                        quoted_delivery_date: {
                            $set: null
                        }
                    },
                    address: {$set: selectedAddress}
                },
                validationErrors: {$set: {}}
            });

            setIsUpdating(false);
        }
    }

    // At the time of writing there is an issue that randomly resets the name of a company in an address,
    // we need to prevent shipments without addresses from being created
    function toggleInvalidAddress() {
        const ShipmentIndex = ctx.shipments.findIndex(({id}) => id === shipmentId);
        ctx.onUpdateWithoutSave({
            validationErrors: {
                $set: {
                    shipping: {
                        [shipmentId]: {
                            invalid_address:
                                'This address does not have a company name, please add one before you can use it.'
                        }
                    }
                }
            },
            shipments: {
                [ShipmentIndex]: {
                    address_id: {$set: undefined},
                    shipping_method_code: {
                        $set: ''
                    },
                    shipping_method_provider: {
                        $set: ''
                    },
                    shipping_method_name: {
                        $set: ''
                    },
                    quoted_delivery_date: {
                        $set: null
                    }
                },
                address: {$set: undefined}
            }
        });
    }

    useEffect(() => {
        const address = ctx.addresses.find(({id}) => id === addressId);

        if (!address?.company) {
            toggleInvalidAddress();
        }
    }, []);

    const shippingAddressError =
        ctx.validationErrors?.shipping?.[shipmentId]?.address_id ||
        ctx.validationErrors?.shipping?.[shipmentId]?.invalid_address ||
        undefined;

    return (
        <div
            className="main-container-shipping-address bg-structure-white"
            data-error={!!shippingAddressError ? 'shipping' : ''}>
            <div
                className={`${
                    ctx.addresses.length > 0
                        ? 'd-flex align-items-start justify-content-between shipping-select-label'
                        : ''
                }`}>
                <div
                    className={`container_shipping-address ${
                        ctx.addresses.length > 0 ? 'm-0' : 'mb-4'
                    }  font-body-sm`}>
                    <IfElse condition={addressId > 0}>
                        <h6 className="color-accent-primary m-0">
                            <i className="fa fa-check me-2" />
                            1. Shipping Address
                        </h6>
                        <span>1. Choose shipping address:</span>
                    </IfElse>
                </div>

                <Button
                    onClick={setIsAddingNewAddress.bind(this, true)}
                    className="container_add-address-btn d-flex align-items-center px-2 add-address-btn"
                    label="Add new address"
                    secondary
                    small
                />
                {isAddingNewAddress && (
                    <CartCheckoutAddressModal
                        formData={{is_shipping_address: true}}
                        onSaveComplete={({address}) => {
                            ctx.onUpdateWithoutSave({addresses: {$push: [address]}});
                            handleChooseShippingAddress(address.id);
                            setIsAddingNewAddress(false);
                        }}
                        onClose={setIsAddingNewAddress.bind(this, false)}
                    />
                )}

                {editingAddressId && (
                    <CartCheckoutAddressModal
                        formData={ctx.addresses.find(({id}) => id === editingAddressId)}
                        onSaveComplete={({address}) => {
                            const addressIndex = ctx.addresses.findIndex(
                                ({id}) => id === address.id
                            );
                            ctx.onUpdateWithoutSave({
                                addresses: {$splice: [[addressIndex, 1, address]]}
                            });
                            handleChooseShippingAddress(address.id);
                            setEditingAddressId(false);
                        }}
                        onClose={setEditingAddressId.bind(this, false)}
                    />
                )}
            </div>
            <If condition={ctx.addresses.length > 0}>
                <div className="mt-2 position-relative">
                    <FilterInput
                        listItemsToShow={5}
                        placeholder="Search and select existing addresses"
                        value={singlePrettyAddress(
                            ctx.addresses.find(({id}) => id === selectedAddressId)
                        )}
                        onChange={handleChooseShippingAddress}
                        error={shippingAddressError}
                        label="*Shipping address"
                        options={
                            prettyAddress(
                                ctx.addresses,
                                true,
                                setEditingAddressId,
                                deleteAddressId => {
                                    const addressIndex = ctx.addresses.findIndex(
                                        ({id}) => id === deleteAddressId
                                    );
                                    ctx.onUpdateWithoutSave({
                                        addresses: {$splice: [[addressIndex, 1]]}
                                    });
                                    if (selectedAddressId === deleteAddressId) {
                                        handleChooseShippingAddress(false);
                                    }
                                    deleteAddressById(deleteAddressId);
                                }
                            ).addresses
                        }
                        name="select_address"
                    />
                    <If condition={isUpdating}>
                        <div className="color-accent-primary">
                            <i className="fa fa-spinner fa-spin" /> saving...
                        </div>
                    </If>
                </div>
            </If>
        </div>
    );
}
